import { Add, Upload } from "@mui/icons-material";
import CheckIcon from "@mui/icons-material/Check";
import {
  Box,
  Button,
  Checkbox,
  FormControlLabel,
  Grid2,
  Paper,
  Typography,
} from "@mui/material";
import grey from "@mui/material/colors/grey";
import { PimoReactComponent } from "@pimo/pimo-app-builder";
import { LazyList } from "@pimo/pimo-components";
import { PMPopUp } from "@pimo/pimo-components";
import { usePimoForm } from "@pimo/pimo-components/src/lib/pimo-components/use-pimo-form/use-pimo-form.hook";
import { getLocalizedMessage } from "@pimo/pimo-l10n";
import type { Dayjs } from "dayjs";
import dayjs from "dayjs";
import { useCallback, useMemo, useState } from "react";
import { v4 as uuidv4 } from "uuid";
import {
  BasicItem,
  Configuration,
  ExtendedFieldError,
  ITEM_NAMES,
  Project,
  PROJECT_NAMES,
} from "vmd-types";
import { checkField, VIEW_NAMES } from "vmd-utils";

import { deleteBasicItem } from "../../helpers/fetch-basic-items";
import { BasicItemRow } from "./basic-item-row";

export type ItemManagerProps = {
  basicItems: BasicItem[];
  projects?: Project[];
  configuration?: Configuration;
  language?: string;
};

export const ItemManagerPage: PimoReactComponent<
  ItemManagerProps,
  {
    basicItems?: BasicItem[];
    configuration?: Configuration;
    projects?: Project[];
  }
> = ({ language = "en" }) => {
  const { setValue, formState } = usePimoForm<{
    basicItems: BasicItem[];
    configuration?: Configuration;
    projects?: Project[];
    validationErrors: Record<string, ExtendedFieldError>;
    enableSaveButton?: boolean;
  }>({
    appStateSlice: "editSettingsForm",
  });

  const [openDelete, setOpenDelete] = useState(false);
  const [currentSelectedItem, setCurrentSelectedItem] = useState<
    string[] | undefined
  >(undefined);

  const [selectedItemIndices, setSelectedItemIndices] = useState<string[]>([]);
  const [showErrorsOnly, setShowErrorsOnly] = useState(false);

  // Ids of non-deleted items that carry a validation error, keyed by their
  // full-array index (validation errors use "basicItems.<idx>.<field>").
  const erroringItemIds = useMemo(() => {
    const erroringIndices = new Set(
      Object.keys(formState.validationErrors ?? {})
        .map((key) => key.match(/^basicItems\.(\d+)\./)?.[1])
        .filter((idx): idx is string => idx !== undefined)
    );
    const ids = new Set<string>();
    formState.basicItems?.forEach((item, idx) => {
      if (item.flag !== "delete" && erroringIndices.has(String(idx)))
        ids.add(item.id);
    });
    return ids;
  }, [formState.basicItems, formState.validationErrors]);

  // Rows to render: all non-deleted items, narrowed to erroring ones when the
  // "errors only" filter is on so a single bad row is findable among thousands.
  const visibleItems = useMemo(() => {
    const items =
      formState.basicItems?.filter((item) => item.flag !== "delete") ?? [];
    return showErrorsOnly
      ? items.filter((item) => erroringItemIds.has(item.id))
      : items;
  }, [formState.basicItems, showErrorsOnly, erroringItemIds]);

  const itemName =
    ITEM_NAMES.find(
      (entry) => entry.single === formState.configuration?.itemName
    ) ?? ITEM_NAMES[0];

  const projectName =
    PROJECT_NAMES.find(
      (entry) => entry.single === formState.configuration?.projectName
    ) ?? PROJECT_NAMES[0];

  // Distribution channel apps are created/edited only in the report; Manage Items just lists them.
  const isDistribution = formState.configuration?.useCase === "distribution";

  const getFieldDefinition = useCallback(
    (fieldName: string) => {
      return formState.configuration?.fieldsDefinition
        ?.filter((f) => f.purpose === "itemBasic")
        ?.find((f) => f.fieldName === fieldName && f.enabled === true);
    },
    [formState.configuration]
  );

  const handleBasicItemFieldChange = useCallback(
    (id: string, fieldKey: string, value: unknown) => {
      const index = formState.basicItems.findIndex((item) => item.id === id);
      if (index === -1) {
        return;
      }

      const newErrors = {
        ...(formState.validationErrors ?? {}),
      } as Record<string, ExtendedFieldError>;

      const setFlagIfNeeded = () => {
        const item = formState.basicItems?.[index];
        if (item && item.flag !== "create" && item.flag !== "delete") {
          setValue(`basicItems.${index}.flag`, "update");
        }
      };

      const def = getFieldDefinition(fieldKey);

      switch (fieldKey) {
        case "name":
        case "description":
        case "customId":
        case "owner": {
          setValue(`basicItems.${index}.${fieldKey}`, value);

          if (!value && def?.isMandatory) {
            newErrors[`basicItems.${index}.${fieldKey}`] = {
              type: "required",
              message: "This field is required",
              tabContainingErrors: VIEW_NAMES[7],
            } as ExtendedFieldError;
          } else {
            delete newErrors[`basicItems.${index}.${fieldKey}`];
          }
          checkField({
            iterableFieldName: `basicItems.{idx}.${fieldKey}`,
            idx: index,
            errors: newErrors,
            setErrors: (errors) => setValue("validationErrors", errors),
            value,
            tabContainingErrors: VIEW_NAMES[7],
          });

          setFlagIfNeeded();
          break;
        }

        case "projectIds": {
          // value is project id string
          const projectIds = value as string;
          if (projectIds === "allProjects") {
            setValue(
              `basicItems.${index}.projectIds`,
              formState.projects?.map((p) => p.id) ?? []
            );
            setValue(`basicItems.${index}.itemAssignedToAllProjects`, true);
            delete newErrors[`basicItems.${index}.projectIds`];
            setValue("validationErrors", newErrors);
            setFlagIfNeeded();
            break;
          } else {
            setValue(`basicItems.${index}.itemAssignedToAllProjects`, false);
            setValue(`basicItems.${index}.projectIds`, [projectIds]);

            if (!projectIds && def?.isMandatory) {
              newErrors[`basicItems.${index}.projectIds`] = {
                type: "required",
                message: "This field is required",
                tabContainingErrors: VIEW_NAMES[7],
              } as ExtendedFieldError;
            } else {
              delete newErrors[`basicItems.${index}.projectIds`];
            }

            setValue("validationErrors", newErrors);
            setFlagIfNeeded();
            break;
          }
        }
        case "budget": {
          const raw = value as string;
          setValue(`basicItems.${index}.budget`, parseFloat(raw));
          if (!raw && def?.isMandatory) {
            newErrors[`basicItems.${index}.budget`] = {
              type: "required",
              message: "This field is required",
              tabContainingErrors: VIEW_NAMES[7],
            } as ExtendedFieldError;
          } else if (raw && isNaN(Number(raw))) {
            newErrors[`basicItems.${index}.budget`] = {
              type: "pattern",
              message: "This field must be a number",
              tabContainingErrors: VIEW_NAMES[7],
            } as ExtendedFieldError;
          } else {
            delete newErrors[`basicItems.${index}.budget`];
          }

          setValue("validationErrors", newErrors);
          setFlagIfNeeded();
          break;
        }
        case "startDate": {
          const dateVal = value as
            | Dayjs
            | string
            | number
            | Date
            | null
            | undefined;
          setValue(`basicItems.${index}.startDate`, dateVal);

          let entry: ExtendedFieldError | undefined;
          if (!dateVal && def?.isMandatory) {
            entry = {
              type: "required",
              message: "This field is required",
              tabContainingErrors: VIEW_NAMES[7],
            };
          } else if (dateVal && !dayjs(dateVal).isValid()) {
            entry = {
              type: "invalid",
              message: "This field is required",
              tabContainingErrors: VIEW_NAMES[7],
            };
          }

          if (entry) {
            newErrors[`basicItems.${index}.startDate`] = entry;
          } else {
            delete newErrors[`basicItems.${index}.startDate`];
          }

          setValue("validationErrors", newErrors);
          setFlagIfNeeded();
          break;
        }
        case "endDate": {
          const dateVal = value as
            | Dayjs
            | string
            | number
            | Date
            | null
            | undefined;
          setValue(`basicItems.${index}.endDate`, dateVal);

          let newEntry: ExtendedFieldError | undefined;
          if (!dateVal && def?.isMandatory) {
            newEntry = {
              type: "required",
              message: "This field is required",
              tabContainingErrors: VIEW_NAMES[7],
            };
          } else if (dateVal && !dayjs(dateVal).isValid()) {
            newEntry = {
              type: "invalid",
              message: "This is not a valid date",
              tabContainingErrors: VIEW_NAMES[7],
            };
          } else if (
            dateVal &&
            formState.basicItems?.[index]?.startDate &&
            dayjs(dateVal).isBefore(
              dayjs(formState.basicItems?.[index]?.startDate)
            )
          ) {
            newEntry = {
              type: "invalid",
              message: "End date should be after start date",
              tabContainingErrors: VIEW_NAMES[7],
            };
          }

          if (newEntry) {
            newErrors[`basicItems.${index}.endDate`] = newEntry;
          } else {
            delete newErrors[`basicItems.${index}.endDate`];
          }

          setValue("validationErrors", newErrors);
          setFlagIfNeeded();
          break;
        }
        case "dropdownFieldOne": {
          const val = value as string;
          setValue(`basicItems.${index}.dropdownFieldOne`, val);

          if (!val && def?.isMandatory) {
            newErrors[`basicItems.${index}.dropdownFieldOne`] = {
              type: "required",
              message: "This field is required",
              tabContainingErrors: VIEW_NAMES[7],
            } as ExtendedFieldError;
          } else {
            delete newErrors[`basicItems.${index}.dropdownFieldOne`];
          }

          setValue("validationErrors", newErrors);
          setFlagIfNeeded();
          break;
        }
        default: {
          setValue(`basicItems.${index}.${fieldKey}`, value);
          setFlagIfNeeded();
          break;
        }
      }
      if (formState.enableSaveButton !== true) {
        setValue("enableSaveButton", true);
      }
    },
    [
      formState.basicItems,
      formState.validationErrors,
      formState.enableSaveButton,
      formState.projects,
      getFieldDefinition,
      setValue,
    ]
  );

  const handleAppend = () => {
    const newItem: BasicItem = {
      id: uuidv4(),
      name: "",
      description: "",
      owner: "",
      budget: 0,
      startDate: undefined,
      endDate: undefined,
      projectIds: formState.projects?.map((p) => p.id) ?? [],
      flag: "create",
      itemAssignedToAllProjects: true,
    };
    setValue("enableSaveButton", true);
    setValue("basicItems", [...(formState?.basicItems ?? []), newItem]);

    const element = document.getElementById("dummy-to-scroll-into");
    element?.scrollIntoView({ behavior: "smooth" });
  };

  const handleDelete = (indices: string[]) => {
    setCurrentSelectedItem(indices);
    setOpenDelete(true);
  };
  const handleConfirmDelete = async () => {
    if (currentSelectedItem === undefined) {
      return;
    }
    const ids = currentSelectedItem;

    if (isDistribution) {
      await Promise.all(ids.map((id) => deleteBasicItem(id)));

      const removed = new Set(ids);
      const oldItems = formState.basicItems ?? [];
      const newItems = oldItems.filter((item) => !removed.has(item.id));
      const newIndexById = new Map(newItems.map((item, i) => [item.id, i]));

      const nextErrors: Record<string, ExtendedFieldError> = {};
      for (const [key, value] of Object.entries(
        formState.validationErrors ?? {}
      )) {
        const match = key.match(/^basicItems\.(\d+)\.(.+)$/);
        if (!match) {
          nextErrors[key] = value;
          continue;
        }
        const item = oldItems[Number(match[1])];
        const newIndex = item ? newIndexById.get(item.id) : undefined;
        if (newIndex === undefined) continue;
        nextErrors[`basicItems.${newIndex}.${match[2]}`] = value;
      }

      setValue("basicItems", newItems);
      setValue("validationErrors", nextErrors);
      setSelectedItemIndices((prev) => prev.filter((id) => !removed.has(id)));
      setOpenDelete(false);
      setCurrentSelectedItem(undefined);
      return;
    }

    setValue(
      "basicItems",
      formState.basicItems.map((item) =>
        item.id && ids.includes(item.id) ? { ...item, flag: "delete" } : item
      )
    );
    setValue("enableSaveButton", true);
    setOpenDelete(false);
  };

  const handleCloseDelete = () => {
    setOpenDelete(false);
    setCurrentSelectedItem(undefined);
  };

  return (
    <Box
      sx={{
        p: 2,
        display: "flex",
        flexDirection: "column",
        gap: 2,
        height: "calc(100vh - 100px)",
        overflowY: "auto",
      }}
    >
      <Typography sx={{ fontSize: "24px", fontWeight: 500 }}>
        {getLocalizedMessage(language, "manageItems.title") ||
          `Manage ${itemName.plural}`}
      </Typography>
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: 2,
          width: "100%",
          mt: 2,
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 2,
            flexWrap: "wrap",
          }}
        >
          <Typography sx={{ color: "#000", fontSize: "16px", fontWeight: 500 }}>
            {getLocalizedMessage(language, "manageItems.listTitle") ||
              `${itemName.plural} List`}{" "}
            (
            {formState.basicItems?.filter((item) => item.flag !== "delete")
              .length ?? 0}{" "}
            {itemName.plural})
          </Typography>
          <FormControlLabel
            control={
              <Checkbox
                checked={showErrorsOnly}
                onChange={(e) => setShowErrorsOnly(e.target.checked)}
                disabled={erroringItemIds.size === 0}
              />
            }
            label={
              getLocalizedMessage(language, "manageItems.showErrorsOnly", {
                count: erroringItemIds.size,
              }) || `Show only items with errors (${erroringItemIds.size})`
            }
            sx={{
              m: 0,
              ".MuiFormControlLabel-label": { fontSize: "14px" },
            }}
          />
        </Box>
        <Paper
          sx={{
            borderRadius: 3,
            boxShadow: "none",
            border: "1px solid",
            borderColor: grey[300],
          }}
        >
          <Grid2
            container
            alignItems="center"
            sx={{
              py: 2,
              paddingLeft: 2,
              borderBottom: "1px solid",
              borderColor: grey[300],
              flex: 0,
            }}
          >
            <Grid2 size={0.5}>
              <Checkbox
                onChange={(e) => {
                  const isChecked = e.target.checked;
                  setSelectedItemIndices(
                    isChecked
                      ? (formState.basicItems?.map((item) => item.id) ?? [])
                      : []
                  );
                }}
                icon={
                  <span
                    style={{
                      width: 20,
                      height: 20,
                      border: "2px solid",
                      borderColor: grey[700],

                      display: "inline-block",
                      backgroundColor: "#fff",
                    }}
                  />
                }
                checkedIcon={
                  <span
                    style={{
                      width: 20,
                      height: 20,

                      border: "2px solid",
                      borderColor: grey[700],
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      backgroundColor: "#fff",
                    }}
                  >
                    <CheckIcon
                      style={{
                        fontSize: 18,
                        color: grey[700],
                      }}
                    />
                  </span>
                }
                checked={
                  selectedItemIndices?.length === formState.basicItems?.length
                }
              />
            </Grid2>
            <Grid2 size={8.5} sx={{ fontSize: "14px", fontWeight: 500 }}>
              {getLocalizedMessage(language, "manageItems.itemName") ||
                itemName.single}
            </Grid2>
            <Grid2
              size={3}
              sx={{
                display: "flex",
                justifyContent: "flex-end",
                gap: 2,
                px: 2,
              }}
            >
              {selectedItemIndices?.length > 0 && (
                <Button
                  variant="outlined"
                  onClick={() => handleDelete(selectedItemIndices)}
                  sx={{
                    textTransform: "none",
                    borderRadius: "4px",
                    padding: "7px 16px",
                    whiteSpace: "nowrap",
                    minWidth: "fit-content",
                  }}
                >
                  {getLocalizedMessage(language, "deleteSelected") ||
                    "Delete Selected"}
                </Button>
              )}

              {!isDistribution && (
                <>
                  <Button
                    variant="outlined"
                    component="label"
                    startIcon={<Upload />}
                    sx={{
                      textTransform: "none",
                      borderRadius: "4px",
                      padding: "7px 16px",
                      mr: 1,
                      whiteSpace: "nowrap",
                      minWidth: "fit-content",
                    }}
                  >
                    {getLocalizedMessage(language, "manageItems.uploadExcel") ||
                      "Upload Excel"}
                    <input
                      type="file"
                      accept=".xlsx,.xls"
                      hidden
                      onChange={(e) => {
                        const file = e.target.files?.[0];
                        if (file) {
                          const event = new CustomEvent("excel-file-selected", {
                            detail: { file },
                          });
                          document.dispatchEvent(event);
                        }
                      }}
                    />
                  </Button>

                  <Button
                    variant="outlined"
                    onClick={handleAppend}
                    startIcon={<Add />}
                    sx={{
                      textTransform: "none",
                      borderRadius: "4px",
                      padding: "7px 16px",
                      whiteSpace: "nowrap",
                      minWidth: "fit-content",
                    }}
                  >
                    {getLocalizedMessage(language, "addItem", {
                      itemName: itemName.single,
                    }) || `Add ${itemName.single}`}
                  </Button>
                </>
              )}
            </Grid2>
          </Grid2>

          <LazyList
            key={showErrorsOnly ? "errors-only" : "all"}
            itemCount={visibleItems.length}
            itemsPerPage={20}
            sx={{
              width: "100%",
              flex: 1,
              minHeight: 0,
              overflowY: "auto",
            }}
          >
            {(start, end) => (
              <>
                {visibleItems?.slice(start, end).map((field, index) => (
                  <BasicItemRow
                    key={field.id}
                    index={index}
                    field={field}
                    itemName={itemName}
                    projectName={projectName}
                    formState={formState}
                    onFieldChange={handleBasicItemFieldChange}
                    setValue={setValue}
                    handleDelete={handleDelete}
                    selectedItemIndices={selectedItemIndices}
                    setSelectedItemIndices={setSelectedItemIndices}
                    currentSelectedItem={currentSelectedItem}
                    setCurrentSelectedItem={setCurrentSelectedItem}
                    language={language}
                  ></BasicItemRow>
                ))}
                {visibleItems.length > 6 && end === visibleItems.length && (
                  <Box sx={{ height: 40 }} id={"dummy-to-scroll-into"}></Box>
                )}
              </>
            )}
          </LazyList>
        </Paper>
      </Box>
      {/* Delete Confirmation Dialog */}
      <PMPopUp
        open={openDelete}
        title={
          getLocalizedMessage(language || "en", "dialog.title.confirmDelete") ||
          "Confirm Delete"
        }
        description={
          currentSelectedItem?.length === 1
            ? getLocalizedMessage(
                language || "en",
                "dialog.message.genericConfirm",
                {
                  itemName: itemName.single,
                }
              ) || `Are you sure you want to delete this ${itemName.single}?`
            : getLocalizedMessage(
                language || "en",
                "dialog.genericConfirmMulti",
                {
                  itemCount: currentSelectedItem?.length ?? 0,
                  itemName: itemName.plural,
                }
              ) ||
              `Are you sure you want to delete these ${currentSelectedItem?.length} ${itemName.plural}?`
        }
        onClose={() => handleCloseDelete()}
        maxWidth="sm"
        cancelButtonText={
          getLocalizedMessage(language || "en", "dialog.button.cancel") ||
          "Cancel"
        }
        cancelButtonFireEvent={() => handleCloseDelete()}
        cancelButtonColor="white"
        confirmationButtonColor="error"
        confirmationButtonText={
          getLocalizedMessage(language || "en", "dialog.button.delete") ||
          "Delete"
        }
        confirmationButtonFireEvent={() => {
          handleConfirmDelete();
        }}
      />
    </Box>
  );
};
